import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCampaigns, getCampaignById, createCampaign, updateCampaign, deleteCampaign, startNewCampaign } from '../../api/services/campaignService';

// Async thunks
export const fetchCampaignsAction = createAsyncThunk(
  'campaign/fetchCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCampaigns();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCampaignByIdAction = createAsyncThunk(
  'campaign/fetchCampaignById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getCampaignById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCampaignAction = createAsyncThunk(
  'campaign/createCampaign',
  async (campaignData, { rejectWithValue }) => {
    try {
      const response = await createCampaign(campaignData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCampaignAction = createAsyncThunk(
  'campaign/updateCampaign',
  async ({ id, campaignData }, { rejectWithValue }) => {
    try {
      const response = await updateCampaign(id, campaignData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCampaignAction = createAsyncThunk(
  'campaign/deleteCampaign',
  async (id, { rejectWithValue }) => {
    try {
      await deleteCampaign(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// New: Start New Campaign thunk
export const startNewCampaignAction = createAsyncThunk(
  'campaign/startNewCampaign',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await startNewCampaign(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  campaigns: [],
  currentCampaign: null,
  loading: false,
  error: null,
  startCampaignResult: null,
};

// Slice
const campaignSlice = createSlice({
  name: 'campaign',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCampaign: (state) => {
      state.currentCampaign = null;
    },
    // Allow clearing start campaign result from UI
    clearStartCampaignResult: (state) => {
      state.startCampaignResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Campaigns
      .addCase(fetchCampaignsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchCampaignsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Campaign by ID
      .addCase(fetchCampaignByIdAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignByIdAction.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload;
      })
      .addCase(fetchCampaignByIdAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Campaign
      .addCase(createCampaignAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCampaignAction.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.push(action.payload);
      })
      .addCase(createCampaignAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Campaign
      .addCase(updateCampaignAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCampaignAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.campaigns.findIndex(campaign => campaign.id === action.payload.id);
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
        state.currentCampaign = action.payload;
      })
      .addCase(updateCampaignAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Campaign
      .addCase(deleteCampaignAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCampaignAction.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(campaign => campaign.id !== action.payload);
        if (state.currentCampaign?.id === action.payload) {
          state.currentCampaign = null;
        }
      })
      .addCase(deleteCampaignAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Start New Campaign
      .addCase(startNewCampaignAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.startCampaignResult = null;
      })
      .addCase(startNewCampaignAction.fulfilled, (state, action) => {
        state.loading = false;
        state.startCampaignResult = action.payload; // { message, data }
        // Optionally add to list if backend returns created entity
        if (action.payload?.data) {
          state.campaigns.push(action.payload.data);
        }
      })
      .addCase(startNewCampaignAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentCampaign, clearStartCampaignResult } = campaignSlice.actions;
export default campaignSlice.reducer;
